import React, { useState, useEffect, useContext } from 'react';
import {
  Button, Badge, Popover, Typography, List, Avatar, Divider, Collapse, Skeleton,
} from 'antd';
import {
  FieldTimeOutlined, DownOutlined, CloseOutlined, LoadingOutlined, UserOutlined,
  SyncOutlined, InfoCircleOutlined, PlusOutlined, DeleteOutlined, CaretRightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import * as api from 'util/api';
import { sentenceCase } from 'change-case';
import numberToWords from 'number-to-words';
import { productProperties, skuProperties } from 'config/language';
import faker from 'faker';
import { MiscDataContext } from 'util/MiscData';
import RecentActivityDetail from './RecentActivityDetail/RecentActivityDetail';
import './RecentActivity.less';

const { Title, Text } = Typography;

const { Panel } = Collapse;

const SKELETON_COUNT = 10;

const EventSkeleton = () => (
  <List.Item className="skeleton-list-item">
    <Skeleton avatar title={false} loading active>
      <List.Item.Meta
        avatar={<Avatar icon={<UserOutlined />} />}
        description={faker.lorem.words(4)}
      />
    </Skeleton>
  </List.Item>
);

const RecentActivity = () => {
  const [hasUnseenActivity, setHasUnseenActivity] = useState(false);
  const [visible, setVisible] = useState(false);
  const [recentActivity, setRecentActivity] = useState(null);
  const [errorLoading, setErrorLoading] = useState(false);

  // Unseen badge comes from page load. Opening the bell fetches recent activity,
  // and that request marks it seen on the server.
  const miscData = useContext(MiscDataContext);
  useEffect(() => {
    if (miscData?.hasUnseenActivity) {
      setHasUnseenActivity(true);
    }
  }, [miscData]);

  useEffect(() => {
    if (!visible) return;
    if (hasUnseenActivity && recentActivity) {
      setHasUnseenActivity(false);
    }
    if (!recentActivity) {
      setErrorLoading(false);
      api.getRecentActivity()
        .then(setRecentActivity)
        .catch(() => {
          setErrorLoading(true);
        });
    }
  }, [visible, recentActivity, hasUnseenActivity]);

  // const onViewMore = () => {
  //   //
  //   console.log('onViewMore');
  // };

  return (
    <span id="recent-activity-popover-container">
      <Popover
        visible={visible}
        onVisibleChange={setVisible}
        overlayClassName="recent-activity-overlay"
        trigger="click"
        content={(
          <>
            <div id="recent-activity-header">
              <Title level={5}>Recent Activity</Title>
              <Button
                id="recent-activity-close-button"
                shape="circle"
                icon={<CloseOutlined />}
                onClick={() => setVisible((current) => !current)}
              />
            </div>
            <Divider />
            {errorLoading && (
              <Text className="placeholder-container">
                Unable to load recent activity, please try again later.
              </Text>
            )}
            <List
              id="recent-activity-list"
              itemLayout="horizontal"
              dataSource={recentActivity || Array.from(Array(SKELETON_COUNT).keys())}
              renderItem={(event) => {
                if ((!errorLoading && !recentActivity) || !event || !event.context) {
                  return (<EventSkeleton />);
                }

                let message;

                const actionBadge = {
                  className: 'generic',
                  icon: <InfoCircleOutlined />,
                };

                // TODO: Make language config and links dynamic based on context
                let languageConfig = {};
                if (event.context === 'product_sku') {
                  languageConfig = skuProperties;
                } else if (event.context === 'product') {
                  languageConfig = productProperties;
                }

                const contextLabel = event.context === 'product_sku'
                  ? 'SKU'
                  : sentenceCase(event.context).toLowerCase();
                const actorName = event.actor?.name || event.user?.name || 'Someone';
                const productLinkId = event.context === 'product_sku'
                  ? event.productId
                  : event.instanceId;

                const identifierLink = (
                  <>
                    {event.type !== 'delete' && productLinkId ? (
                      <Link to={`/dashboard/product-database/products/${productLinkId}/edit`}>
                        <Button
                          type="link"
                          className="inline-link-button"
                          onClick={() => setVisible(false)}
                        >
                          {event.instanceName}
                        </Button>
                      </Link>
                    ) : (
                      event.instanceName
                    )}
                  </>
                );

                if (event.type === 'create') {
                  message = (
                    <>
                      {`${actorName} added the ${contextLabel} `}
                      {identifierLink}
                      {(event.changes || []).length > 1 ? ` and set ${numberToWords.toWords(event.changes.length)} properties.` : '.'}
                    </>
                  );
                  actionBadge.className = 'create';
                  actionBadge.icon = <PlusOutlined />;
                } else if (event.type === 'update') {
                  message = (event.changes || []).length === 1 ? (
                    <RecentActivityDetail
                      languageData={languageConfig[event.changes[0].field]}
                      field={event.changes[0].field}
                      previousValue={event.changes[0].previousValue}
                      newValue={event.changes[0].newValue}
                      userName={actorName}
                      contextLabel={contextLabel}
                      identifierLink={identifierLink}
                    />
                  ) : (
                    <>
                      {`${actorName} updated ${numberToWords.toWords((event.changes || []).length)} properties for the ${contextLabel} `}
                      {identifierLink}
                      .
                    </>
                  );
                  actionBadge.className = 'update';
                  actionBadge.icon = <SyncOutlined />;
                } else if (event.type === 'delete') {
                  message = (
                    <>
                      {`${actorName} deleted the ${contextLabel} `}
                      {identifierLink}
                      .
                    </>
                  );
                  actionBadge.className = 'delete';
                  actionBadge.icon = <DeleteOutlined />;
                }

                return (
                  <>
                    <List.Item>
                      <List.Item.Meta
                        avatar={(
                          <Badge
                            count={(
                              <div className={`action-badge ${actionBadge.className}`}>
                                {actionBadge.icon}
                              </div>
                              )}
                          >
                            <Avatar
                              icon={<UserOutlined />}
                              src={event.actor?.photoUrl || event.user?.profilePhotoUrl}
                            />
                          </Badge>
                          )}
                        title={(
                          <>
                            <Text className="event-message-container">{message}</Text>
                            {(event.changes || []).length > 1 && (
                              <Collapse
                                ghost
                              >
                                <Panel
                                  header={`View changes (${event.changes.length})`}
                                  key="1"
                                  showArrow
                                >
                                  <ul>
                                    {event.changes.map((change) => (
                                      <li key={change.field}>
                                        <RecentActivityDetail
                                          {...change}
                                          languageData={languageConfig[change.field]}
                                        />
                                      </li>
                                    ))}
                                  </ul>
                                </Panel>
                              </Collapse>
                            )}
                          </>
                            )}
                        description={dayjs().to(event.timestamp)}
                      />
                    </List.Item>
                  </>
                );
              }}
            />
            {/* TODO: Enable loading more */}
            {/* <div id="view-more-button-container">
              <Button
                disabled={!recentActivity || errorLoading}
                type="link"
                onClick={onViewMore}
                icon={<DownOutlined />}
              >
                View more
              </Button>
            </div> */}
          </>
        )}
      >
        <div id="recent-activity-button-container">
          <Badge dot={hasUnseenActivity} offset={[-4, 6]}>
            <Button
              id="recent-activity-button"
              shape="circle"
              icon={<FieldTimeOutlined id="recent-activity-icon" />}
            />
          </Badge>
        </div>
      </Popover>
    </span>
  ); };

export default RecentActivity;
